import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItemData {
  id: string;
  type: ToastType;
  message: string;
  duration?: number | undefined;
}

export interface ToastContextValue {
  addToast: (toast: Omit<ToastItemData, 'id'>) => void;
  removeToast: (id: string) => void;
  toast: {
    success: (message: string, duration?: number | undefined) => void;
    error: (message: string, duration?: number | undefined) => void;
    info: (message: string, duration?: number | undefined) => void;
    warning: (message: string, duration?: number | undefined) => void;
  };
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItemData[]>([]);

  const addToast = useCallback((toast: Omit<ToastItemData, 'id'>) => {
    const id = crypto.randomUUID();
    const newToast: ToastItemData = { ...toast, id };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toastHelpers = useMemo(
    () => ({
      success: (message: string, duration?: number | undefined) =>
        addToast(duration !== undefined ? { type: 'success', message, duration } : { type: 'success', message }),
      error: (message: string, duration?: number | undefined) =>
        addToast(duration !== undefined ? { type: 'error', message, duration } : { type: 'error', message }),
      info: (message: string, duration?: number | undefined) =>
        addToast(duration !== undefined ? { type: 'info', message, duration } : { type: 'info', message }),
      warning: (message: string, duration?: number | undefined) =>
        addToast(duration !== undefined ? { type: 'warning', message, duration } : { type: 'warning', message }),
    }),
    [addToast],
  );

  const value = useMemo(
    () => ({
      addToast,
      removeToast,
      toast: toastHelpers,
    }),
    [addToast, removeToast, toastHelpers],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: ToastItemData[];
  onRemove: (id: string) => void;
}) {
  return (
    <div
      className="fixed bottom-5 right-5 z-50 flex flex-col gap-2.5 max-w-sm w-full pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} onRemove={onRemove} />
      ))}
    </div>
  );
}

export function ToastItem({
  toast,
  onRemove,
}: {
  toast: ToastItemData;
  onRemove: (id: string) => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => onRemove(toast.id), toast.duration ?? 4500);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  const typeConfig = {
    success: {
      border: 'border-emerald-500/30',
      bg: 'bg-slate-900/95',
      icon: '✓',
      iconBg: 'bg-emerald-500/20 text-emerald-400',
    },
    error: {
      border: 'border-red-500/30',
      bg: 'bg-slate-900/95',
      icon: '✕',
      iconBg: 'bg-red-500/20 text-red-400',
    },
    info: {
      border: 'border-blue-500/30',
      bg: 'bg-slate-900/95',
      icon: 'ℹ',
      iconBg: 'bg-blue-500/20 text-blue-400',
    },
    warning: {
      border: 'border-amber-500/30',
      bg: 'bg-slate-900/95',
      icon: '⚠',
      iconBg: 'bg-amber-500/20 text-amber-400',
    },
  }[toast.type];

  return (
    <div
      className={`pointer-events-auto flex items-center gap-3 rounded-xl border p-3.5 shadow-cardhover backdrop-blur-md transition-all animate-log-slide ${typeConfig.border} ${typeConfig.bg}`}
      role="alert"
    >
      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${typeConfig.iconBg}`}>
        {typeConfig.icon}
      </div>
      <span className="flex-1 text-xs font-medium text-slate-100">{toast.message}</span>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className="rounded-lg p-1 text-slate-400 hover:text-slate-200 transition-colors"
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
